import { ReactNode } from "react";
import { CarouselProvider, Carousel, CarouselControl } from "./Carousel";
import { DESKTOP_MEDIA_QUERY } from "@/common/constants";
import { useMediaQuery } from "@/utils/hooks";
import { graphql, useFragment } from "react-relay";
import EventCard from "./EventCard";
import { EventsCarouselFragment$key } from "./__generated__/EventsCarouselFragment.graphql";
import { Link } from "@/kit/ui/link";
import { ChevronRightIcon } from "@/icons";
import { ReaderNode } from "relay-runtime/util/ReaderNode";

const DEFAULT_VISIBLE_ITEMS = 3;

const Fragment = graphql`
  fragment EventsCarouselFragment on EventConnection {
    edges {
      node {
        id
        ...EventCardFragment
      }
    }
  }
`;

interface Props {
  events: EventsCarouselFragment$key;
  icon: ReactNode;
  label: ReactNode;
  to: string;
  hidden?: boolean;
  action?: ReactNode;
  description?: ReactNode;
  order?: ReaderNode;
}

export function EventsCarousel({
  icon,
  label,
  hidden = false,
  ...props
}: Props) {
  const events = useFragment(Fragment, props.events);
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);

  if (!events.edges.length) return null;

  return (
    <CarouselProvider
      itemCount={Math.max(
        events.edges.length,
        isDesktop ? DEFAULT_VISIBLE_ITEMS : 1,
      )}
      visible={isDesktop ? DEFAULT_VISIBLE_ITEMS : 1}
    >
      <div className="flex flex-col items-start gap-5">
        <div className="w-full flex flex-row items-start justify-between">
          <div className="w-full flex flex-col items-start gap-1 px-2">
            <div className="w-full flex flex-row items-center gap-2">
              <div>{icon}</div>
              <div className="w-full flex flex-row items-center justify-between">
                <Link
                  to={props.to}
                  variant="link"
                  hue="grey"
                  size="md"
                  className="inline-flex items-center text-sm"
                >
                  <p className="text-2xl text-grey-900 font-semibold leading-8">
                    {label}
                  </p>
                  <ChevronRightIcon />
                </Link>
                {props.order && <>{props.order}</>}
                {events.edges.length >
                  (isDesktop ? DEFAULT_VISIBLE_ITEMS : 1) &&
                  !hidden && <CarouselControl />}
              </div>
            </div>
            {props.description && (
              <p className="text-grey-700 text-base font-normal leading-6">
                {props.description}
              </p>
            )}
          </div>
          {props.action && <>{props.action}</>}
        </div>
        {!hidden && (
          <Carousel
            items={events.edges}
            renderItem={(item, index) => (
              <EventCard key={index} event={item.node} />
            )}
          />
        )}
      </div>
    </CarouselProvider>
  );
}
